import { useShallow } from 'zustand/react/shallow';

import { getProducts } from '@/shared/api/market/market.api';
import {
  getCloudStorageValue,
  setCloudStorageValue,
} from '@/shared/helpers/cloud-storage';
import { createClearableStore } from '@/shared/helpers/create-clearable-store';

import {
  type MarketItem,
  MarketItemType,
  type MarketState,
  ShopTab,
} from './market.types';

const defaultMarketState = {
  main: undefined,
  partner: undefined,
  hasNewPartnerProducts: false,
  hasNewInnerProducts: false,
};

const filterSupportedOffers = (marketItems?: MarketItem[]) => {
  return marketItems?.filter(
    (item) => !!item.id && !!item.name && !!item.amount && !!item.price
  );
};

const useMarketStateRaw = createClearableStore<MarketState>()((set, get) => ({
  ...defaultMarketState,
  actions: {
    hydrate: (fresh: Partial<MarketState>) => set(fresh),
    fetchProducts: async () => {
      try {
        const categories = await getProducts();

        const main = categories?.find((c) => c.key === 'MAIN');
        const partner = categories?.find((c) => c.key === 'PARTNER');

        const res = {
          main: filterSupportedOffers(main?.content ?? []),
          partner: filterSupportedOffers(partner?.content ?? []),
        };

        set(res);
        void get().actions.checkNewProducts(ShopTab.PARTNER_DEALS);
        return res;
      } catch (err) {
        console.error('ERROR in MARKET -> fetchProducts', err);
        return [];
      }
    },
    checkNewProducts: async (tab: ShopTab) => {
      const { partner } = get();

      if (!partner) {
        return [];
      }

      // for now the indication is connected to the front only and depends on the availability of external tribute goods
      const hasExternalItem = partner.some(
        (item) => item.type === MarketItemType.EXTERNAL
      );

      const hasNewProductsInTab = await getCloudStorageValue(tab);

      // flag not found, means new products have not been shown to the user yet
      if (hasNewProductsInTab === null) {
        if (tab === ShopTab.FOR_YOU) {
          // actions for displaying notification in the tab FOR_YOU
        } else {
          set({ hasNewPartnerProducts: hasExternalItem });
          hasExternalItem && void setCloudStorageValue(ShopTab.PARTNER_DEALS, 'true');
        }
      } else {
        if (tab === ShopTab.FOR_YOU) {
          // actions for displaying notification in the tab FOR_YOU
        } else {
          set({
            hasNewPartnerProducts: hasExternalItem && hasNewProductsInTab === 'true',
          });
        }
      }
    },
  },
}));

export const useMarketState = () =>
  useMarketStateRaw(
    useShallow((state) => ({
      main: state.main,
      partner: state.partner,
      hasNewPartnerProducts: state.hasNewPartnerProducts,
      hasNewInnerProducts: state.hasNewInnerProducts,
    }))
  );

export const useMarketActions = () =>
  useMarketStateRaw(useShallow((state) => state.actions));
